// src/app/components/prompt-tracking/delete-confirm-dialog.tsx

import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

interface DeleteConfirmDialogProps {
  promptText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DeleteConfirmDialog({
  promptText,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 md:p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="w-full rounded-2xl shadow-2xl"
        style={{
          backgroundColor: 'var(--background)',
          border: '1px solid var(--border)',
          maxWidth: 'min(440px, calc(100dvw - 24px))',
        }}
      >
        {/* Alert-style header */}
        <div
          className="px-4 md:px-6 pt-5 md:pt-6 pb-4 rounded-t-2xl"
          style={{ backgroundColor: 'var(--infrared-coral-10)' }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--infrared-coral-20, rgba(239,68,68,0.2))' }}
            >
              <AlertTriangle
                className="w-5 h-5"
                style={{ color: 'var(--infrared-coral)' }}
              />
            </div>
            <div className="min-w-0">
              <h3
                style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--foreground)',
                  marginBottom: '4px',
                }}
              >
                Delete this prompt?
              </h3>
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                This will permanently delete the prompt and all of its analysis history. This
                action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        {/* Prompt preview */}
        <div
          className="px-4 md:px-6 py-4"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--muted-foreground)',
              marginBottom: '6px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Prompt to delete:
          </p>
          <p
            className="px-3 py-2 rounded-lg"
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--foreground)',
              backgroundColor: 'var(--muted)',
              border: '1px solid var(--border)',
              lineHeight: '1.5',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            "{promptText}"
          </p>
        </div>

        {/* Actions — stack on mobile */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-3 px-4 md:px-6 py-4">
          <Button variant="outline" onClick={onCancel} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="w-full sm:w-auto"
            style={{
              backgroundColor: 'var(--infrared-coral)',
              color: 'white',
              border: 'none',
            }}
          >
            Delete Prompt
          </Button>
        </div>
      </div>
    </div>
  );
}